import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { UploadSimple, FileArrowUp, DownloadSimple } from "@phosphor-icons/react";

const norm = (s) => String(s ?? "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");

function parseNumber(v) {
  if (typeof v === "number") return v;
  const s = String(v ?? "").replace(/[R$\s%]/g, "");
  if (!s) return 0;
  const n = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  const f = parseFloat(n);
  return isNaN(f) ? 0 : f;
}

function rowsToItems(rows, fields) {
  if (!rows.length) return [];
  const headerIdx = rows[0].map((h) => {
    const n = norm(h);
    return fields.findIndex((f) => n === norm(f.label) || n === norm(f.name) || norm(f.label).startsWith(n) && n.length > 2);
  });
  const hasHeader = headerIdx.filter((i) => i >= 0).length >= Math.min(2, fields.length);
  const dataRows = hasHeader ? rows.slice(1) : rows;
  return dataRows
    .filter((r) => r.some((c) => String(c ?? "").trim() !== ""))
    .map((r) => {
      const item = {};
      fields.forEach((f, fi) => {
        const col = hasHeader ? headerIdx.indexOf(fi) : fi;
        const raw = col >= 0 ? r[col] : undefined;
        if (f.type === "number") item[f.name] = raw === undefined || raw === "" ? (f.default ?? 0) : parseNumber(raw);
        else item[f.name] = raw === undefined || String(raw).trim() === "" ? (f.default ?? "") : String(raw).trim();
      });
      return item;
    });
}

function parseText(text) {
  return text.split(/\r?\n/).map((line) => {
    const sep = line.includes("\t") ? "\t" : line.includes(";") ? ";" : ",";
    return line.split(sep).map((c) => c.trim());
  });
}

export function ImportDialog({ open, onOpenChange, title, endpoint, fields, dataTestId, onDone, transform, hint }) {
  const [text, setText] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const applyRows = (rows) => {
    const parsed = rowsToItems(rows, fields);
    setItems(parsed);
    if (!parsed.length) toast.error("Nenhuma linha válida encontrada");
  };

  const onText = (v) => { setText(v); setItems(rowsToItems(parseText(v), fields)); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: "" });
      setText("");
      applyRows(rows);
      toast.success(`Arquivo lido: ${file.name}`);
    } catch (err) {
      toast.error("Não foi possível ler o arquivo");
    } finally {
      e.target.value = "";
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([fields.map((f) => f.label)]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Modelo");
    XLSX.writeFile(wb, `modelo-${endpoint}.xlsx`);
  };

  const invalid = items.filter((i) => fields.some((f) => f.required && (i[f.name] === "" || i[f.name] === undefined)));

  const save = async () => {
    const valid = items.filter((i) => !invalid.includes(i));
    if (!valid.length) return toast.error("Nenhum item válido para importar");
    setSaving(true);
    try {
      const r = await api.post(`/${endpoint}/bulk`, transform ? valid.map(transform) : valid);
      toast.success(`${r.data.created} itens importados!`);
      setText(""); setItems([]);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl" data-testid={`${dataTestId}-import-dialog`}>
        <DialogHeader>
          <DialogTitle>Importar lista - {title}</DialogTitle>
          <DialogDescription>
            Cole linhas copiadas do Excel/planilha (colunas separadas por Tab, ; ou ,) ou envie um arquivo CSV/XLSX.
            Ordem das colunas: <span className="font-medium text-slate-700">{fields.map((f) => f.label).join(" | ")}</span>
            {hint && <span className="block mt-1">{hint}</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            value={text}
            onChange={(e) => onText(e.target.value)}
            rows={6}
            placeholder={`Ex:\n${fields.map((f) => f.placeholder || f.label).join("; ")}`}
            className="font-mono text-xs"
            data-testid={`${dataTestId}-import-text`}
          />
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden" onChange={onFile} data-testid={`${dataTestId}-import-file`} />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid={`${dataTestId}-import-file-btn`}>
              <FileArrowUp size={16} className="mr-1" /> Enviar arquivo CSV/XLSX
            </Button>
            <Button type="button" variant="ghost" onClick={downloadTemplate} data-testid={`${dataTestId}-import-template`}>
              <DownloadSimple size={16} className="mr-1" /> Baixar modelo
            </Button>
          </div>

          {items.length > 0 && (
            <div className="card-crisp overflow-auto max-h-64" data-testid={`${dataTestId}-import-preview`}>
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-600">
                    {fields.map((f) => <th key={f.name} className="px-3 py-2 font-medium">{f.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className={`border-t border-slate-100 ${invalid.includes(it) ? "bg-rose-50" : ""}`}>
                      {fields.map((f) => <td key={f.name} className={`px-3 py-2 ${f.type === "number" ? "font-mono-nums" : ""}`}>{String(it[f.name])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {items.length > 0 && (
            <p className="text-xs text-slate-500" data-testid={`${dataTestId}-import-summary`}>
              {items.length - invalid.length} item(ns) prontos para importar
              {invalid.length > 0 && <span className="text-rose-600"> · {invalid.length} ignorado(s) por campos obrigatórios vazios</span>}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="button" onClick={save} disabled={saving || items.length - invalid.length === 0} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`${dataTestId}-import-save`}>
            <UploadSimple size={16} className="mr-1" /> {saving ? "Importando..." : `Importar ${items.length - invalid.length || ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
